import { useCallback, useState } from "react"

import { useDropzone } from "react-dropzone"
import { useTranslation } from "react-i18next"

import { Upload } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"
import { ChooseTranslationLanguagesMultiSelect } from "@/features/video-insight/components/choose-translation-languages-multi-select"
import { useCurrentSessionType } from "@/hooks/use-current-session-type"
import { cn } from "@/lib/utils"
import { useVideoInsightStore } from "@/stores/video-insight-store"

import { VideoLanguageSelect } from "./video-language-select"

const MAX_FILE_SIZE = 30 * 1024 * 1024 * 1024 // 30GB in bytes

const VIDEO_FORMATS = [
  ".flv",
  ".mxf",
  ".gxf",
  ".ts",
  ".ps",
  ".3gp",
  ".3gpp",
  ".mpg",
  ".wmv",
  ".asf",
  ".avi",
  ".mp4",
  ".m4a",
  ".m4v",
  ".isma",
  ".ismv",
  ".dvr-ms",
  ".mkv",
  ".wav",
  ".mov",
]

type UploadVideoProps = {
  onUpload: (file: File) => void
  onFileAdded: (file: File) => void
  isLoading: boolean
  selectedLanguages: string[]
  setSelectedLanguages: (languages: string[]) => void
}

export function UploadVideo({
  onUpload,
  onFileAdded,
  isLoading,
  selectedLanguages,
  setSelectedLanguages,
}: UploadVideoProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const sessionType = useCurrentSessionType()

  const videoStore = useVideoInsightStore(sessionType)
  const videoLanguage = videoStore((state) => state.videoLanguage)

  const { t } = useTranslation()

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0]
    if (file.size > MAX_FILE_SIZE) {
      alert(t("pages.video_insight.file_size_limit"))
      return
    }
    setSelectedFile(file)
    onFileAdded(file)
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled: isLoading,
    accept: {
      "video/*": VIDEO_FORMATS,
    },
    maxFiles: 1,
  })

  return (
    <section className="flex flex-col space-y-6">
      <h1 className="text-foreground mb-2 text-center text-4xl font-semibold tracking-tight">
        {t("pages.video_insight.title", { defaultValue: "Video Insight" })}
      </h1>
      <section className="flex flex-col gap-6">
        <section className="flex flex-col gap-2">
          <Label>{t("pages.video_insight.upload_video")}</Label>
          <section
            {...getRootProps()}
            className={cn(
              "border-border min-h-[210px] cursor-pointer",
              "hover:border-accent gap-4 rounded-lg border-2 border-dashed p-4",
              "transition-colors duration-200 ease-in-out",
              {
                "border-accent": isDragActive,
                "hover:border-border cursor-not-allowed": isLoading,
              }
            )}
          >
            <input {...getInputProps()} />

            <section
              className={cn("flex h-full flex-col items-center justify-between gap-4", {
                "justify-start gap-2": isDragActive,
              })}
            >
              <section className="flex w-full flex-col items-center gap-2">
                <Upload className={"text-foreground size-12"} />
                {!isDragActive && (
                  <section className="flex w-full flex-col items-center text-center">
                    {selectedFile ? (
                      <Card className="bg-background w-full md:max-w-[75%]">
                        <CardHeader>
                          <CardTitle className="text-lg font-bold">
                            {t("pages.video_insight.selected_video")}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="line-clamp-1">
                            <span className="font-bold">{t("pages.video_insight.name")}:</span>{" "}
                            {selectedFile.name}
                          </p>
                          <p className="line-clamp-1">
                            <span className="font-bold">{t("pages.video_insight.size")}:</span>{" "}
                            {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                          </p>
                        </CardContent>
                      </Card>
                    ) : (
                      <p className="text-foreground text-lg font-medium">
                        {t("pages.video_insight.drag_drop_prompt")}
                      </p>
                    )}
                  </section>
                )}
              </section>
              {!isDragActive && (
                <section className="text-muted-foreground mt-2 flex w-full flex-col items-center gap-2 text-xs">
                  <section className="flex w-full flex-col items-center gap-2">
                    <p>{t("pages.video_insight.supported_formats")}:</p>
                    <ul className="flex max-w-full flex-wrap justify-center gap-1.5">
                      {VIDEO_FORMATS.map((format) => (
                        <li key={format}>
                          <Badge
                            className="px-2 py-0.5 font-mono text-[10px] uppercase"
                            variant="secondary"
                          >
                            {format.replace(/^\./, "")}
                          </Badge>
                        </li>
                      ))}
                    </ul>
                  </section>
                  <p>{t("pages.video_insight.max_file_size")}</p>
                </section>
              )}
              {isDragActive && (
                <p className="text-foreground text-center text-lg font-medium">
                  {t("pages.video_insight.drop_here")}
                </p>
              )}
            </section>
          </section>
        </section>

        <VideoLanguageSelect />
        <ChooseTranslationLanguagesMultiSelect
          selectedLanguages={selectedLanguages}
          setSelectedLanguages={setSelectedLanguages}
        />
        <Button
          className="w-full text-base disabled:cursor-not-allowed disabled:opacity-50"
          disabled={!selectedFile || isLoading || !videoLanguage}
          size="lg"
          onClick={() => {
            if (selectedFile) {
              onUpload(selectedFile)
            }
          }}
        >
          {isLoading ? <Spinner /> : t("pages.video_insight.transcribe")}
        </Button>
      </section>
    </section>
  )
}
